import React, { useMemo, useState } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { get, groupBy, isArray, isEmpty, sortBy } from "lodash";
import Location from "../../../../../components/Common/Display/Location";
import VinWithType from "../../../../../components/Common/Display/VinWithType";

type TProps = {
    loads: any[];
    t: any;
    /**
     * Optional: hide a row (ex: marked for delete in update form)
     */
    isRowHidden?: (row: any) => boolean;
    /**
     * Optional: show per-row errors (ex: validation errors by order id)
     */
    getRowErrors?: (row: any) => string[];
    /**
     * Optional: custom action cell renderer (ex: delete/undo)
     */
    renderActionCell?: (row: any) => React.ReactNode;
    /**
     * Optional: show action column header + cells
     */
    showActionColumn?: boolean;
};

const getLocationLabel = (location: any): string => {
    const city = get(location, 'city');
    const code = get(location, 'location_code');
    const landmark = get(location, 'landmark');
    const country = get(location, 'country_sort') || get(location, 'country');

    if ( !isEmpty(city) && !isEmpty(country) ) {
        return `${ city } (${ country })`;
    }
    if ( !isEmpty(city) ) {
        return String(city);
    }
    if ( !isEmpty(code) ) {
        return String(code);
    }
    if ( !isEmpty(landmark) ) {
        return String(landmark);
    }
    return '-';
};

export default function GroupedLoadsByRoute(props: TProps) {
    const { loads, t, isRowHidden, getRowErrors, renderActionCell, showActionColumn } = props;

    const safeLoads = isArray(loads) ? loads : [];

    const groups = useMemo(() => {
        const grouped = groupBy(safeLoads, (row: any) => {
            const fromId = String(get(row, 'from_location.id') || '');
            const toId = String(get(row, 'to_location.id') || '');
            return `${fromId}___${toId}`;
        });

        const keys = Object.keys(grouped || {});
        const items = keys.map((key: string) => {
            const rows = grouped[key] || [];
            const from = get(rows, '0.from_location');
            const to = get(rows, '0.to_location');
            const fromLabel = getLocationLabel(from);
            const toLabel = getLocationLabel(to);
            return {
                key,
                from,
                to,
                fromLabel,
                toLabel,
                rows,
                count: rows.length,
            };
        });

        return sortBy(items, [(i: any) => i.fromLabel, (i: any) => i.toLabel]);
    }, [safeLoads]);

    const [activeIndex, setActiveIndex] = useState<any>(() => {
        // open first panel by default (if any)
        return groups.length > 0 ? [0] : [];
    });

    if ( safeLoads.length <= 0 ) {
        return (
            <div className="text-muted small">
                { t("no records found") }
            </div>
        );
    }

    return (
        <Accordion
            multiple
            activeIndex={ activeIndex }
            onTabChange={ (e: any) => setActiveIndex(e.index) }
        >
            { groups.map((g: any, gIdx: number) => {
                const header = (
                    <div className="d-flex align-items-center justify-content-between w-100">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <strong className="text-uppercase">
                                { g.fromLabel } → { g.toLabel }
                            </strong>
                            <span className="text-muted small">
                                ({ g.count } { (t && typeof t === 'function') ? t('vehicles') : 'vehicles' })
                            </span>
                        </div>
                        <div className="text-muted small d-none d-md-block">
                            <span className="me-1"><Location item={ g.from } titled={ 1 } /></span>
                            <span> <Location item={ g.to } titled={ 1 } /></span>
                        </div>
                    </div>
                );

                return (
                    <AccordionTab key={ g.key || gIdx } header={ header }>
                        <div className="table-responsive">
                            <table className={ "fl-table w-full" }>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{ t("load number") }</th>
                                        <th>{ t("vin number") }</th>
                                        <th>{ t("status") }</th>
                                        <th>{ t("brand") }</th>
                                        <th>{ t("model") }</th>
                                        <th>{ t("supplier compound") }</th>
                                        <th>{ t("dealer") }</th>
                                        { showActionColumn && <th className="text-end">{ t("action") }</th> }
                                    </tr>
                                </thead>
                                <tbody>
                                    { (g.rows || []).map((row: any, idx: number) => {
                                        if ( isRowHidden && isRowHidden(row) ) {
                                            return null;
                                        }

                                        const vin = get(row, 'alp_outbound_load_orders.0.order.vin_number') || "-";
                                        const brand = get(row, 'alp_outbound_load_orders.0.order.brand.title') || "-";
                                        const model = get(row, 'alp_outbound_load_orders.0.order.brand_model.title') || "-";
                                        const errors = getRowErrors ? (getRowErrors(row) || []) : [];

                                        return (
                                            <tr key={ row.id ?? idx }>
                                                <td>{ idx + 1 }</td>
                                                <td className="text-uppercase"><strong>{ row.load_number || "-" }</strong></td>
                                                <td className="text-uppercase">
                                                    <VinWithType vinNumber={ vin } />
                                                    {
                                                        !isEmpty(errors) &&
                                                        <div className="text-danger small mt-1">
                                                            { (errors || []).join(', ') }
                                                        </div>
                                                    }
                                                </td>
                                                <td className="text-uppercase">{ row.status || "-" }</td>
                                                <td className="text-uppercase">{ brand }</td>
                                                <td className="text-uppercase">{ model }</td>
                                                <td className="text-uppercase">{ row.supplier_compound || "-" }</td>
                                                <td className="text-uppercase">{ row.dealer_name || "-" }</td>
                                                { showActionColumn && <td className="text-end">{ renderActionCell ? renderActionCell(row) : null }</td> }
                                            </tr>
                                        );
                                    }) }
                                </tbody>
                            </table>
                        </div>
                    </AccordionTab>
                );
            }) }
        </Accordion>
    );
}

